export interface ServiceSuggestion {
  clientId: string;
  clientName: string;
  averageIntervalDays: number;
  lastServiceDate: string;
  nextSuggestedDate: string;
  totalCompleted: number;
  confidence: 'alto' | 'medio' | 'baixo';
}

const COMPLETED_STATUSES = new Set(['completed', 'concluído', 'concluido']);

const diffInDays = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));

const calculateConfidence = (intervals: number[]) => {
  if (intervals.length < 2) return 'baixo';
  const avg = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
  const variance =
    intervals.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / intervals.length;
  const stdDev = Math.sqrt(variance);
  const variation = stdDev / (avg || 1);
  if (variation < 0.2) return 'alto';
  if (variation < 0.4) return 'medio';
  return 'baixo';
};

export const buildServiceSuggestions = (appointments: any[]): ServiceSuggestion[] => {
  const grouped = new Map<
    string,
    { clientName: string; dates: Date[] }
  >();

  appointments
    .filter((apt) => COMPLETED_STATUSES.has((apt.status || '').toLowerCase()))
    .forEach((apt) => {
      const clientId = apt.client_id || apt.clientId;
      if (!clientId || !apt.scheduled_date) return;
      const clientName = apt.client_name || apt.clientName || 'Cliente';
      const date = new Date(apt.scheduled_date);
      if (!grouped.has(clientId)) {
        grouped.set(clientId, { clientName, dates: [] });
      }
      grouped.get(clientId)!.dates.push(date);
    });

  const suggestions: ServiceSuggestion[] = [];
  const today = new Date();

  grouped.forEach(({ clientName, dates }, clientId) => {
    if (dates.length < 3) return; // Precisa de histórico suficiente
    const sortedDates = dates.sort((a, b) => a.getTime() - b.getTime());
    const intervals: number[] = [];
    for (let i = 1; i < sortedDates.length; i++) {
      intervals.push(diffInDays(sortedDates[i], sortedDates[i - 1]));
    }
    if (intervals.length === 0) return;
    const avgInterval = Math.round(intervals.reduce((sum, val) => sum + val, 0) / intervals.length);
    const lastServiceDate = sortedDates[sortedDates.length - 1];
    const nextSuggestedDate = new Date(lastServiceDate);
    nextSuggestedDate.setDate(nextSuggestedDate.getDate() + avgInterval);
    if (nextSuggestedDate < today) {
      // Recomendação imediata
      nextSuggestedDate.setDate(today.getDate());
    }
    suggestions.push({
      clientId,
      clientName,
      averageIntervalDays: avgInterval,
      lastServiceDate: lastServiceDate.toISOString(),
      nextSuggestedDate: nextSuggestedDate.toISOString(),
      totalCompleted: dates.length,
      confidence: calculateConfidence(intervals),
    });
  });

  return suggestions.sort(
    (a, b) => new Date(a.nextSuggestedDate).getTime() - new Date(b.nextSuggestedDate).getTime()
  );
};

